import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Stethoscope, Translate, CurrencyInr, CalendarCheck } from "@phosphor-icons/react";

const SPECIALTIES = ["all", "General Medicine", "Cardiology", "Diabetology", "Pediatrics", "Dermatology", "Orthopedics"];
const TIME_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [specialty, setSpecialty] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);

  const load = () => api.get("/doctors", { params: { specialty, q } }).then((r) => setDoctors(r.data));
  useEffect(() => { load(); }, [specialty]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const book = async () => {
    if (!selected) return;
    setBooking(true);
    try {
      // Build ISO from local date parts (no UTC round-trip) — otherwise India
      // (UTC+5:30) rolls the date back one day before the fetch.
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const slotISO = new Date(`${y}-${m}-${d}T${slot}:00`).toISOString();
      await api.post("/appointments", {
        doctor_id: selected.id, slot_iso: slotISO, mode: "video", notes,
      });
      toast.success(`Appointment booked with ${selected.name}`);
      setSelected(null); setNotes("");
    } catch (e) {
      toast.error("Could not book appointment");
    } finally { setBooking(false); }
  };

  return (
    <div className="p-6 md:p-12 pb-24 md:pb-12">
      <div className="mb-8">
        <div className="overline text-terracotta">Verified clinicians</div>
        <h1 className="font-heritage text-5xl text-ayurveda mt-1">Meet the team</h1>
        <p className="text-charcoal/70 mt-2">Book with registered doctors across specialties. Fees are transparent.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <Input placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} className="md:max-w-xs bg-white" data-testid="doctors-search" />
        <Select value={specialty} onValueChange={setSpecialty}>
          <SelectTrigger className="md:max-w-xs bg-white" data-testid="doctors-specialty"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All specialties" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {doctors.map((d) => (
          <Card key={d.id} className="border-black/5 bg-white overflow-hidden hover:-translate-y-1 transition-transform duration-300" data-testid={`doctor-card-${d.id}`}>
            <div className="h-40 bg-ayurveda relative overflow-hidden">
              <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover opacity-90" />
              <Badge className="absolute top-3 left-3 bg-terracotta text-white">{d.specialty}</Badge>
            </div>
            <CardContent className="p-5 space-y-3">
              <div>
                <h3 className="font-clinical text-lg text-ayurveda">{d.name}</h3>
                <div className="text-xs text-charcoal/60">Reg: {d.registration_no} · {d.experience_years}+ yrs</div>
              </div>
              <p className="text-sm text-charcoal/70 leading-relaxed line-clamp-3">{d.bio}</p>
              <div className="flex items-center justify-between pt-2 text-sm">
                <div className="flex items-center gap-1 text-charcoal/70"><Translate size={14} /> {d.languages?.slice(0,2).join(", ")}</div>
                <div className="flex items-center gap-1 font-clinical text-ayurveda"><CurrencyInr size={14} /> {d.fee}</div>
              </div>
              <Button className="w-full bg-ayurveda hover:bg-ayurveda/90 text-bone" onClick={() => setSelected(d)} data-testid={`book-btn-${d.id}`}>
                <CalendarCheck size={16} className="mr-2" /> Book appointment
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-heritage text-3xl text-ayurveda">Book with {selected?.name}</DialogTitle>
            <DialogDescription className="text-sm text-charcoal/60">
              {selected?.specialty} · ₹{selected?.fee} · Video consult
            </DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6 py-4">
            <div>
              <div className="overline text-terracotta mb-2">Pick a date</div>
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(d) => d < new Date(Date.now() - 86400000)} className="rounded-lg border border-black/10" data-testid="book-calendar" />
            </div>
            <div className="space-y-4">
              <div>
                <div className="overline text-terracotta mb-2">Time slot</div>
                <div className="grid grid-cols-3 gap-2">
                  {TIME_SLOTS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSlot(s)}
                      className={`py-2 rounded-md border text-sm ${slot === s ? "bg-ayurveda text-bone border-ayurveda" : "border-black/10 hover:border-ayurveda"}`}
                      data-testid={`slot-${s}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="overline text-terracotta mb-2">Notes for the doctor (optional)</div>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What would you like to discuss?" data-testid="book-notes" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} data-testid="book-cancel">Cancel</Button>
            <Button onClick={book} disabled={booking} className="bg-ayurveda hover:bg-ayurveda/90 text-bone" data-testid="book-confirm">
              {booking ? "Booking…" : `Confirm — ₹${selected?.fee}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
